import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { GridFilterInputDate } from "@mui/x-data-grid";
import { Typography, Box, CircularProgress, Alert, Tooltip, Button, TextField, IconButton, MenuItem, Select, InputLabel, FormControl, Checkbox, Autocomplete } from "@mui/material";
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
 
import { UserContext } from '../components/MiniDrawer';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
 
import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DownloadIcon from '@mui/icons-material/Download';
import { getAllAbsencesForMonth } from '../utils/absenceService';
import { excludeForecastWorklogs, excludeForecastActivities } from '../utils/worklogFilters';
import {
  downloadAbsenceCalendarWorkbook,
  downloadAllProjectsAbsenceCalendarWorkbook,
  sortMembersForCalendar,
} from '../utils/absenceCalendarExport';

function getColumnsFromData(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  // Colonne principali flat secondo il nuovo tracciato
  return [
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      type: 'date',
      filterable: true,
      valueFormatter: (value) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) > new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) < new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort', 
      width: 100,
      valueFormatter: (value) => {
        if (!value) return '';
        const hours = Math.round((Number(value) / 60) * 100) / 100;
        return `${hours} h`;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 180 },
    { field: 'status_name', headerName: 'Status', width: 140 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    { field: 'user_name', headerName: 'User', width: 180 },
    { field: 'description', headerName: 'Description', width: 250 },
  ];
}

export default function MonthlyManagerView() {
  const { selectedUser } = useContext(UserContext);
  const { isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, isDeliveryManager, currentRole } = useRoleAccess();
  const { userId } = useCurrentUser();
  const navigate = useNavigate();
  
  // Debug log per verificare i ruoli
  console.log('MonthlyManagerView - Roles:', { isAdmin, isDeliveryManager, isServiceManager, isTeamLeader, isJRServiceManager, currentRole });
  
  // Fallback: controlla direttamente se il ruolo è 7 (DELIVERY_MANAGER)
  const isDeliveryManagerDirect = currentRole === 7;
  const [rows, setRows] = useState([]); // legacy state (unused)
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false); // legacy
  const [error, setError] = useState(null); // legacy
  const [openDialog, setOpenDialog] = useState(false); // legacy
  const [editingRow, setEditingRow] = useState(null); // legacy
  const [formData, setFormData] = useState({});
  const [projects, setProjects] = useState([]);
  const [projectsInitialized, setProjectsInitialized] = useState(false);
  const [users, setUsers] = useState([]); // legacy
  const [statusList, setStatusList] = useState([]); // legacy
  const [activities, setActivities] = useState([]);
  
  // ============================================================================
  // TEAM LEADER FILTERING LOGIC - COMMENTED OUT (TEMPORARY)
  // Questa logica limita il Team Leader a vedere solo i membri del suo team.
  // Attualmente commentata per allineare il comportamento a Service Manager.
  // Per ripristinare: decommenta tutte le sezioni marcate con [TL_FILTER]
  // ============================================================================
  // const [teamLeaderProjectMembers, setTeamLeaderProjectMembers] = useState({});
  // const [teamLeaderMembersLoaded, setTeamLeaderMembersLoaded] = useState(false);
  
  // TEMPORARY: Imposta sempre come loaded (nessun filtraggio team)
  // useMemo per evitare che l'oggetto vuoto venga ricreato ad ogni render (causerebbe loop infinito)
  const teamLeaderProjectMembers = useMemo(() => ({}), []);
  const teamLeaderMembersLoaded = true;

  // Refs per evitare chiamate duplicate
  const isFetchingProjects = useRef(false);
  const isFetchingActivities = useRef(false);
  const isFetchingTeamLeaderMembers = useRef(false);
  const lastTeamLeaderMembersKey = useRef('');
  const isFetchingPivotData = useRef(false);
  const lastPivotFetchKey = useRef('');
  const isFetchingProjectDetails = useRef(false);
  const lastProjectDetailsKey = useRef('');

  // Filtri per progetto e intervallo date (ora a selezione multipla)
  const [filterProjects, setFilterProjects] = useState([]);
  // Filtro per Activity (multi) - memorizza gli ID
  const [filterActivityIds, setFilterActivityIds] = useState([]);
  // Sostituisco i filtri data con mese/anno
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1); // 1-12
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const yearOptions = [];
  for (let y = 2020; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  // Carica tutti i progetti all'avvio per il filtro
  useEffect(() => {
    // Evita fetch fino a quando il ruolo corrente non è determinato
    if (currentRole === null || currentRole === undefined) {
      setProjects([]);
      setProjectsLoaded(false);
      return;
    }

    // Evita chiamate duplicate
    if (isFetchingProjects.current) {
      return;
    }

    const shouldLoadAllProjects = isDeliveryManager || isDeliveryManagerDirect;
    const shouldLoadManagedProjects = !shouldLoadAllProjects && (isAdmin || isServiceManager || isTeamLeader || isJRServiceManager);

    // Svuota lo stato solo per i ruoli che vedono una lista filtrata
    setProjectsInitialized(false);

    if (!shouldLoadAllProjects) {
      setProjects([]);
      setFilterProjects([]);
    }
    setProjectsLoaded(false);

    isFetchingProjects.current = true;
    console.log('Role check - determinate fetch strategy:', {
      currentRole,
      shouldLoadManagedProjects,
      userId
    });

    if (shouldLoadAllProjects) {
      // Delivery Manager vede tutti i progetti
      console.log('Fetching ALL projects for Delivery Manager');
      fetch(API_ENDPOINTS.PROJECTS)
        .then(res => res.json())
        .then(data => {
          const arr = data.projects || [];
          setProjects(Array.isArray(arr) ? arr : []);
          setProjectsLoaded(true);
          setProjectsInitialized(true);
          isFetchingProjects.current = false;
        })
        .catch(() => {
          setProjects([]);
          setProjectsLoaded(true);
          setProjectsInitialized(true);
          isFetchingProjects.current = false;
        });
      return;
    }

    if (shouldLoadManagedProjects) {
      // Admin, Service Manager, Team Leader, JR Service Manager vedono solo progetti con ruoli 1,2,3
      console.log('Fetching managed projects for userId:', userId);
      fetch(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}&role_ids=1,2,3`)
        .then(res => res.json())
        .then(data => {
          console.log('Managed project users API response:', data);
          if (Array.isArray(data.user_projects_with_references)) {
            const projects = data.user_projects_with_references.map(item => ({
              id: item.project.id,
              name: item.project.name,
              description: item.project.description,
              active: item.project.active,
              project_code: item.project.project_code,
              customer: item.customer,
              project_role: item.project_role // Salva il ruolo dell'utente per questo progetto
            }));

            setProjects(projects);
            setProjectsLoaded(true);
            setProjectsInitialized(true);
            isFetchingProjects.current = false;
          } else {
            setProjects([]);
            setProjectsLoaded(true);
            setProjectsInitialized(true);
            isFetchingProjects.current = false;
          }
        })
        .catch(() => {
          setProjects([]);
          setProjectsLoaded(true);
          setProjectsInitialized(true);
          isFetchingProjects.current = false;
        });
      return;
    }

    // Altri ruoli usano la logica originale con filtro per user_id
    const url = `${API_ENDPOINTS.PROJECTS}?user_id=${userId}`;
    console.log('Fetching user projects for other role:', url);
    fetch(url)
      .then(res => res.json())
      .then(data => {
        const arr = data.projects || [];
        setProjects(Array.isArray(arr) ? arr : []);
        setProjectsLoaded(true);
        setProjectsInitialized(true);
        isFetchingProjects.current = false;
      })
      .catch(() => {
        setProjects([]);
        setProjectsLoaded(true);
        setProjectsInitialized(true);
        isFetchingProjects.current = false;
      });
  }, [currentRole, isAdmin, isDeliveryManager, isDeliveryManagerDirect, isServiceManager, isTeamLeader, isJRServiceManager, userId]);

  // Imposta automaticamente "Select All" per ruoli autorizzati
  useEffect(() => {
    if ((isAdmin || isDeliveryManager || isDeliveryManagerDirect || isServiceManager || isTeamLeader || isJRServiceManager) && projects.length > 0) {
      // Imposta tutti i progetti come selezionati (Select All)
      const allProjectNames = projects.map(p => p.name);
      setFilterProjects(allProjectNames);
      console.log('Auto-setting Select All for authorized projects:', allProjectNames);
    }
  }, [projects, isAdmin, isDeliveryManager, isDeliveryManagerDirect, isServiceManager, isTeamLeader, isJRServiceManager]);

  // Carica Activities per il filtro
  useEffect(() => {
    if (isFetchingActivities.current || activities.length > 0) {
      return;
    }
    isFetchingActivities.current = true;
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        const arr = excludeForecastActivities(data.activities || []);
        setActivities(Array.isArray(arr) ? arr : []);
        isFetchingActivities.current = false;
      })
      .catch(() => {
        setActivities([]);
        isFetchingActivities.current = false;
      });
  }, [activities.length]);

  // Helper: verifica se l'utente è TEAM_LEADER per un progetto specifico
  const isTeamLeaderForProject = (project) => {
    if (!project || !project.project_role) return false;
    const roleCode = project.project_role.code || '';
    return roleCode.toUpperCase() === 'T';
  };

  // Helper: verifica se l'utente ha un ruolo manager (SERVICE_MANAGER, etc.) per un progetto specifico
  const isManagerForProject = (project) => {
    if (!project) return false;
    // Se non c'è project_role, considera il progetto come manager (fallback per compatibilità)
    if (!project.project_role) {
      // Se l'utente è ADMIN, considera tutti i progetti come manager
      return isAdmin;
    }
    const roleId = project.project_role.id;
    const roleCode = project.project_role.code || '';
    // Se è Team Leader (role_code 'T'), non è manager per i dati
    if (roleCode.toUpperCase() === 'T') return false;
    // Ruoli manager: 1 (Service Manager), 2 (JR Service Manager)
    // Nota: 3 è Team Leader, quindi escluso sopra
    return [1, 2].includes(roleId);
  };

  // ============================================================================
  // [TL_FILTER] TEAM LEADER MEMBERSHIP LOADING - COMMENTED OUT
  // Questo useEffect carica i membri dei team per filtrare i worklogs.
  // Attualmente disabilitato per far vedere al TL tutti gli inserimenti.
  // ============================================================================
  /*
  useEffect(() => {
    let cancelled = false;

    const loadTeamLeaderMembers = async () => {
      // Se i progetti non sono ancora caricati, non fare nulla (aspetta che arrivino)
      if (projects.length === 0) {
        // NON impostare teamLeaderMembersLoaded = true qui!
        // Lascialo false finché i progetti non sono caricati
        return;
      }
      
      // Se non c'è userId, non possiamo caricare i membri
      if (!userId) {
        if (!cancelled) {
          setTeamLeaderProjectMembers({});
          setTeamLeaderMembersLoaded(true);
        }
        return;
      }

      // Filtra solo i progetti dove l'utente è TEAM_LEADER
      const teamLeaderProjects = projects.filter(p => isTeamLeaderForProject(p));
      
      if (teamLeaderProjects.length === 0) {
        if (!cancelled) {
          setTeamLeaderProjectMembers({});
          setTeamLeaderMembersLoaded(true);
        }
        return;
      }

      // Crea una chiave univoca basata su userId e project IDs (solo progetti team leader)
      const projectIdsKey = teamLeaderProjects.map(p => p.id).sort().join(',');
      const fetchKey = `${userId}-${projectIdsKey}`;

      // Evita chiamate duplicate per la stessa combinazione
      if (isFetchingTeamLeaderMembers.current) {
        return;
      }

      // Se la chiave è la stessa, evita la chiamata (dati già caricati)
      if (lastTeamLeaderMembersKey.current === fetchKey) {
        return;
      }

      // Reset del flag se la chiave è diversa (nuovi parametri)
      if (lastTeamLeaderMembersKey.current && lastTeamLeaderMembersKey.current !== fetchKey) {
        isFetchingTeamLeaderMembers.current = false;
      }

      isFetchingTeamLeaderMembers.current = true;
      lastTeamLeaderMembersKey.current = fetchKey;
      setTeamLeaderMembersLoaded(false);
      const membershipMap = {};

      for (const project of teamLeaderProjects) {
        try {
          // Il filtro è ora gestito completamente dal backend, quindi passiamo sempre user_id per i progetti team leader
          const userParam = userId ? `&user_id=${userId}` : '';
          const res = await fetch(`${API_ENDPOINTS.TEAMS}?project_id=${project.id}&include_members=true${userParam}`);
          if (!res.ok) {
            continue;
          }
          const data = await res.json();
          const teams = data.teams || [];
          
          const memberIdSet = new Set();
          const teamIdSet = new Set();
          const memberTeamMap = {};

          // I team sono già filtrati dal backend (solo quelli di cui l'utente è team leader)
          teams.forEach((team) => {
            const members = Array.isArray(team.members) ? team.members : [];
            teamIdSet.add(team.id);
            members.forEach((member) => {
              const user = member.user || member;
              const memberId = Number(user?.id || member?.user_id);
              if (!memberId) return;
              memberIdSet.add(memberId);
              const teamName = team.name || '';
              if (teamName) {
                if (!memberTeamMap[memberId]) {
                  memberTeamMap[memberId] = new Set();
                }
                memberTeamMap[memberId].add(teamName);
              }
            });
          });

          if (memberIdSet.size > 0) {
            const projectId = Number(project.id);
            membershipMap[projectId] = {
              memberIds: Array.from(memberIdSet),
              memberIdsSet: memberIdSet,
              teamIds: Array.from(teamIdSet),
              memberTeamMap: Object.fromEntries(
                Object.entries(memberTeamMap).map(([key, value]) => [key, Array.from(value)])
              ),
            };
          }
        } catch (error) {
          console.error(`Error loading team membership for project ${project.id}:`, error);
        }
      }

      if (!cancelled) {
        setTeamLeaderProjectMembers(membershipMap);
        setTeamLeaderMembersLoaded(true);
        isFetchingTeamLeaderMembers.current = false;
      }
    };

    loadTeamLeaderMembers();

    return () => {
      cancelled = true;
    };
  }, [projects, userId]);
  */

  // Rimosso fetchWorklogs standalone per evitare dipendenze instabili

  useEffect(() => {
    if (!selectedUser) return;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        let url;
        if (isAdmin) {
          url = selectedUser === 'ALL'
            ? API_ENDPOINTS.WORKLOGS
            : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
        } else if (isDeliveryManager || isDeliveryManagerDirect) {
          // Delivery Manager vede TUTTI i worklogs del sistema
          url = selectedUser === 'ALL'
            ? API_ENDPOINTS.WORKLOGS
            : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
        } else if (isServiceManager || isTeamLeader || isJRServiceManager) {
          if (projects.length > 0) {
            const projectIds = projects.map(p => p.id);
            const projectIdsParam = projectIds.join(',');
            url = selectedUser === 'ALL'
              ? `${API_ENDPOINTS.WORKLOGS}?project_ids=${projectIdsParam}`
              : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}&project_ids=${projectIdsParam}`;
          } else {
            setRows([]);
            setColumns([]);
            setLoading(false);
            return;
          }
        } else {
          url = selectedUser === 'ALL'
            ? API_ENDPOINTS.WORKLOGS
            : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
        }
        const response = await fetch(url);
        if (!response.ok) throw new Error('Error fetching data');
        const data = await response.json();
        const arr = excludeForecastWorklogs(data.worklogs || []);
        let filteredArr = arr;
        if (isServiceManager || isTeamLeader || isJRServiceManager) {
          const validProjectIds = projects.map(p => p.id);
          filteredArr = arr.filter(row => validProjectIds.includes(row.project_id || row.project?.id));
        }
        // Delivery Manager vede tutti i worklogs senza filtri
        setRows((selectedUser === 'ALL' ? filteredArr : filteredArr.filter(row => String(row.user_id || row.user?.id) === String(selectedUser))).map((row, idx) => ({ id: row.id || idx, ...row })));
        setColumns(getColumnsFromData(filteredArr));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [selectedUser, isAdmin, isDeliveryManager, isDeliveryManagerDirect, isServiceManager, isTeamLeader, isJRServiceManager, userId, projects]);

  // Filtro rimosso - ora usiamo solo la tabella pivot filtrata

  // Ricava i campi editabili (escludi id, created_at, updated_at)
  // const getEditableFields = () => {
  //   if (!columns.length) return [];
  //   return columns.filter(col => !['id', 'created_at', 'updated_at'].includes(col.field));
  // };

  // Gestione apertura dialog
  const handleOpenDialog = (row = null) => {
    setEditingRow(row);
    setFormData(row ? { ...row } : { user: selectedUser });
    setOpenDialog(true);
    // Fetch projects
    fetch(API_ENDPOINTS.PROJECTS)
      .then(res => res.json())
      .then(data => {
        let arr = data.projects || [];
        setProjects(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setProjects([]));
    // Fetch users
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let usersArr = data.users || [];
        setUsers(Array.isArray(usersArr) ? usersArr : []);
      })
      .catch(() => setUsers([]));
    // Fetch activities
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        let arr = excludeForecastActivities(data.activities || []);
        setActivities(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setActivities([]));
    // Fetch status
    fetch(API_ENDPOINTS.STATUS)
      .then(res => res.json())
      .then(data => {
        let arr = data.status || [];
        setStatusList(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setStatusList([]));
    // Effort types non più necessari per questa vista
  };

  // Rimuovo la funzione handleExportExcel

  // Azioni e colonne
  const statusCol = columns.find(col => col.field === 'status_name');
  const otherCols = columns.filter(col => col.field !== 'status_name');
  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    renderCell: (params) => (
      <Box>
        {params.row.status_name === 'Approved' ? (
          <Tooltip title="Blocked">
            <span>
              <IconButton disabled style={{ color: '#bdbdbd' }}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Edit">
            <span>
              <IconButton onClick={() => handleOpenDialog(params.row)}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {params.row.status_name === 'Approved' ? (
          <Tooltip title="Blocked">
            <span>
              <IconButton disabled style={{ color: '#bdbdbd' }}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Delete">
            <span>
              <IconButton onClick={() => handleDelete(params.row.id)}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    )
  };
  const columnsWithActions = statusCol
    ? [actionsColumn, ...otherCols, statusCol]
    : [actionsColumn, ...otherCols];

  // Tooltip per la colonna Description
  const columnsWithDescriptionTooltip = columnsWithActions.map(col => {
    if (col.field === 'description') {
      return {
        ...col,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} placement="top" arrow>
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%'
            }}>{params.value}</span>
          </Tooltip>
        )
      };
    }
    return col;
  });

  // Dialog helpers
  // Dialog handlers not used in this view
  const handleCloseDialog = () => {};
  const handleFormChange = () => {};
  const handleSave = async () => {};
  const handleDelete = async (id) => {};

  // Stato per la tabella pivot
  const [pivotData, setPivotData] = useState({});
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  // const [pivotMode, setPivotMode] = useState('user'); // 'user' o 'project'
  // const [pivotDetails, setPivotDetails] = useState({});
  // Stato per il progetto selezionato per i dettagli
  const [selectedProjectForDetails, setSelectedProjectForDetails] = useState(null);
  const [detailsPivotData, setDetailsPivotData] = useState({});
  const [projectUsers, setProjectUsers] = useState([]);
  // Stato per Team details
  const [projectTeams, setProjectTeams] = useState([]);
  const [teamMembershipByUsername, setTeamMembershipByUsername] = useState({}); // username -> teamName (primo team)
  const [teamPivotData, setTeamPivotData] = useState({});
  // Espansione dettaglio per Team (ora supporta più team)
  const [selectedTeamsForDetails, setSelectedTeamsForDetails] = useState([]);
  // Indicatore di caricamento per i dettagli del team
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);
  
  // Evita flash di progetti non autorizzati prima che siano caricati
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [absencesByUserId, setAbsencesByUserId] = useState({});
  const [exportAbsencesLoading, setExportAbsencesLoading] = useState(false);
  const [exportAllAbsencesLoading, setExportAllAbsencesLoading] = useState(false);

  const projectsForBulkAbsenceExport = useMemo(() => {
    if (isDeliveryManager || isDeliveryManagerDirect) {
      return projects || [];
    }
    return (projects || []).filter((p) => isManagerForProject(p));
  }, [projects, isAdmin, isDeliveryManager, isDeliveryManagerDirect]);

  const handleExportProjectAbsences = () => {
    if (!selectedProjectForDetails || !projectUsers?.length) return;
    setExportAbsencesLoading(true);
    try {
      const members = sortMembersForCalendar(projectUsers, teamMembershipByUsername);
      downloadAbsenceCalendarWorkbook({
        projectName: selectedProjectForDetails,
        year: filterYear,
        month: filterMonth,
        members,
        absencesByUserId,
      });
    } catch (err) {
      console.error('Export absences failed:', err);
      window.alert(err?.message || 'Export absences failed. Please try again.');
    } finally {
      setExportAbsencesLoading(false);
    }
  };

  const handleExportAllProjectsAbsences = async () => {
    if (!projectsForBulkAbsenceExport.length) return;
    setExportAllAbsencesLoading(true);
    try {
      await downloadAllProjectsAbsenceCalendarWorkbook({
        projects: projectsForBulkAbsenceExport,
        year: filterYear,
        month: filterMonth,
        absencesByUserId,
        scopeLabel: 'All_projects',
      });
    } catch (err) {
      console.error('Export all projects absences failed:', err);
      window.alert(err?.message || 'Export absences failed. Please try again.');
    } finally {
      setExportAllAbsencesLoading(false);
    }
  };

  const canExportAllProjectsAbsences =
    projectsLoaded &&
    projectsForBulkAbsenceExport.length > 0;

  // Reset della selezione progetto quando cambiano SOLO i progetti filtrati
  // NON resettare quando cambiano mese/anno o activities (i dati si aggiornano automaticamente)
  useEffect(() => {
    setSelectedProjectForDetails(null);
    setProjectTeams([]); // Svuota i team quando si resetta la selezione
    setTeamMembershipByUsername({});
    setTeamPivotData({});
    setDetailsPivotData({});
    setProjectUsers([]);
    setTeamDetailsLoading(false);
    setSelectedTeamsForDetails([]);
  }, [filterProjects]);
  
  // Svuota i team IMMEDIATAMENTE quando cambia il progetto selezionato (prima che il useEffect principale venga eseguito)
  // Questo previene la visualizzazione di team non autorizzati durante il cambio di progetto
  useEffect(() => {
    // Svuota sempre i team e i dati correlati quando cambia selectedProjectForDetails, anche se è null
    // Il useEffect principale li ricaricherà se necessario
    setProjectTeams([]);
    setTeamMembershipByUsername({});
    setTeamPivotData({});
    setDetailsPivotData({});
    setProjectUsers([]);
  }, [selectedProjectForDetails]);

  // Calcola giorni del mese selezionato
  const daysInMonth = useMemo(() => {
    return new Date(filterYear, filterMonth, 0).getDate();
  }, [filterYear, filterMonth]);

  // Dimensioni fisse per tabella responsive con scroll orizzontale
  const tableDimensions = useMemo(() => {
    const firstColWidth = 140; // px
    const dayColWidth = 45; // px per ciascun giorno
    const totalColWidth = 60; // px
    const minWidth = firstColWidth + daysInMonth * dayColWidth + totalColWidth;
    return { firstColWidth, dayColWidth, totalColWidth, minWidth };
  }, [daysInMonth]);

  const userIdByUsername = useMemo(() => {
    const map = {};
    (projectUsers || []).forEach(u => {
      const uname = u.username || u.name || (u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : null);
      if (uname && u.id) map[uname] = String(u.id);
    });
    Object.entries(detailsPivotData || {}).forEach(([username, days]) => {
      if (map[username]) return;
      const firstDay = Object.values(days)[0];
      if (firstDay?.user_id) map[username] = String(firstDay.user_id);
    });
    return map;
  }, [projectUsers, detailsPivotData]);

  const isUserAbsent = (username, dayNumber) => {
    const uid = userIdByUsername[username];
    if (!uid) return null;
    return absencesByUserId[uid]?.get(dayNumber) || null;
  };

  // Funzione per formattare i nomi utente (abbrevia solo la prima parte)
  const formatUserName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return fullName;
    
    const parts = fullName.trim().split(' ');
    if (parts.length <= 1) return fullName;
    
    // Abbrevia solo la prima parte: prima lettera + punto
    const firstPart = parts[0];
    const abbreviatedFirst = firstPart.charAt(0) + '.';
    
    // Mantieni il resto del nome
    const restOfName = parts.slice(1).join(' ');
    
    return `${abbreviatedFirst} ${restOfName}`;
  };

  // Calcola i nomi dei giorni della settimana per il mese selezionato
  const weekDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
  }, [filterYear, filterMonth, daysInMonth]);

  // Calcola indici dei weekend per il mese selezionato
  const weekendIndexes = useMemo(() => {
    return weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay(); // 0=Sunday, 6=Saturday
      return (day === 0 || day === 6);
    });
  }, [weekDays, filterYear, filterMonth]);

  useEffect(() => {
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    getAllAbsencesForMonth(yearMonth).then(entries => {
      const byUser = {};
      entries.forEach(v => {
        const day = parseInt(v.date.slice(8, 10), 10);
        if (!byUser[String(v.user_id)]) byUser[String(v.user_id)] = new Map();
        byUser[String(v.user_id)].set(day, v);
      });
      setAbsencesByUserId(byUser);
    }).catch(() => setAbsencesByUserId({}));
  }, [filterYear, filterMonth]);

  // Tabella principale: sempre per progetto (All)
  useEffect(() => {
    if (isAdmin || isServiceManager || isTeamLeader || isJRServiceManager || isDeliveryManager || isDeliveryManagerDirect) {
      if (!projectsInitialized) {
        console.log('Projects not initialized yet, skipping pivot data fetch');
        return;
      }
      if (!projectsLoaded || projects.length === 0) {
        console.log('Projects not loaded yet, skipping pivot data fetch');
        return;
      }
    }

    // Verifica se ci sono progetti team leader che necessitano di caricamento membri
    const teamLeaderProjects = projects.filter(p => isTeamLeaderForProject(p));
    
    // ============================================================================
    // [TL_FILTER] MEMBERSHIP FILTERING - COMMENTED OUT
    // Questa logica filtrava i progetti TL basandosi sulla membership del team.
    // Ora il TL vede tutti gli inserimenti come un Service Manager.
    // ============================================================================
    /*
    if (teamLeaderProjects.length > 0 && !teamLeaderMembersLoaded) {
      return;
    }
    
    // Filtra solo i progetti Team Leader che hanno effettivamente una membership assegnata
    // (l'utente deve essere TL E membro di un team per quel progetto)
    const teamLeaderProjectsWithMembership = teamLeaderProjects.filter(project => {
      const projectId = Number(project.id);
      return teamLeaderProjectMembers[projectId]?.memberIdsSet?.size > 0;
    });
    
    // Log per debug: mostra progetti TL esclusi perché senza team assegnato
    const excludedProjects = teamLeaderProjects.filter(project => {
      const projectId = Number(project.id);
      return !teamLeaderProjectMembers[projectId]?.memberIdsSet?.size;
    });
    if (excludedProjects.length > 0) {
      console.log('[TeamLeader] Progetti esclusi (TL senza team assegnato):', 
        excludedProjects.map(p => p.name));
    }
    
    // Procedi: i progetti TL senza membership verranno ignorati nelle fetch successive
    */
    
    // TEMPORARY: TL vede TUTTI i progetti, non solo quelli con membership
    const teamLeaderProjectsWithMembership = teamLeaderProjects;

    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const selectedProjectIds = filterProjects.length === 0
      ? projects.map(p => p.id).sort().join(',')
      : projects.filter(p => filterProjects.includes(p.name)).map(p => p.id).sort().join(',');
    const activityIdsKey = (filterActivityIds && filterActivityIds.length > 0) ? filterActivityIds.sort().join(',') : 'all';
    
    // ============================================================================
    // [TL_FILTER] MEMBERSHIP KEY IN CACHE - COMMENTED OUT
    // Il membershipKey invalidava la cache quando i membri venivano caricati.
    // Non più necessario con il filtraggio disabilitato.
    // ============================================================================
    // const membershipKey = Object.keys(teamLeaderProjectMembers).sort().join(',');
    // const fetchKey = `${yearMonth}-${selectedProjectIds}-${activityIdsKey}-${membershipKey}`;
    
    // TEMPORARY: Nessun membershipKey nella cache key
    const fetchKey = `${yearMonth}-${selectedProjectIds}-${activityIdsKey}`;

    // Evita chiamate duplicate per la stessa combinazione di parametri
    if (isFetchingPivotData.current) {
      return;
    }

    // Se la chiave è la stessa, evita la chiamata (dati già caricati)
    if (lastPivotFetchKey.current === fetchKey) {
      return;
    }

    // Reset del flag se la chiave è diversa (nuovi parametri)
    if (lastPivotFetchKey.current && lastPivotFetchKey.current !== fetchKey) {
      isFetchingPivotData.current = false;
    }

    isFetchingPivotData.current = true;
    lastPivotFetchKey.current = fetchKey;
    setPivotLoading(true);
    setPivotError(null);
    setPivotData({});

    const fetchPivotDataByProjectRole = async () => {
      try {
        const byProject = {};
        const projectsById = projects.reduce((acc, project) => {
          acc[project.id] = project;
          return acc;
        }, {});

        // Separa i progetti per ruolo: team leader vs manager
        const selectedProjects = filterProjects.length === 0
          ? projects
          : projects.filter((p) => filterProjects.includes(p.name));

        // Per Delivery Manager, tutti i progetti sono "manager projects" (vede tutti i worklog)
        const teamLeaderProjects = (isDeliveryManager || isDeliveryManagerDirect) 
          ? [] 
          : selectedProjects.filter(p => isTeamLeaderForProject(p));
        
        // Manager: progetti dove l'utente è SERVICE_MANAGER, JR_SERVICE_MANAGER, ADMIN, o DELIVERY_MANAGER
        // Escludi i progetti team leader perché hanno già una logica diversa
        const managerProjects = selectedProjects.filter(p => {
          if (isTeamLeaderForProject(p)) return false; // Escludi se è team leader
          // Se l'utente è ADMIN o DELIVERY_MANAGER, include tutti i progetti (tranne quelli team leader)
          if (isAdmin || isDeliveryManager || isDeliveryManagerDirect) return true;
          return isManagerForProject(p);
        });

        const activityParam =
          filterActivityIds && filterActivityIds.length > 0 ? `&activity_ids=${filterActivityIds.join(',')}` : '';

        // Gestisci progetti manager (tutti i worklog del progetto)
        // Per Delivery Manager, include tutti i progetti selezionati
        if (managerProjects.length > 0) {
          const managerProjectIds = managerProjects.map((p) => p.id);
        const requestBody = {
          year_month: yearMonth,
            project_ids: managerProjectIds,
          };

        if (filterActivityIds && filterActivityIds.length > 0) {
          requestBody.activity_ids = filterActivityIds;
        }
        
        const response = await fetch(`${API_BASE_URL}/monthly_project_effort_summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
            body: JSON.stringify(requestBody),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API Error: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        if (Array.isArray(data.data)) {
            data.data.forEach((item) => {
            const projectName = item.project_name || (item.project && item.project.name) || 'Unknown Project';
            if (!byProject[projectName]) byProject[projectName] = {};
            const day = new Date(item.work_date).getDate();
            byProject[projectName][day] = item;
          });
          }
        }

        // ============================================================================
        // [TL_FILTER] TEAM LEADER FILTERING LOGIC - COMMENTED OUT
        // Questa logica filtra i worklogs basandosi sui membri del team.
        // Ora il TL vede tutti gli inserimenti come un Service Manager.
        // ============================================================================
        /*
        // Gestisci progetti team leader (solo membri del team)
        for (const project of teamLeaderProjects) {
          // Usa sempre project.id come number per la chiave (coerente con come viene salvato)
          const projectId = Number(project.id);
          const membership = teamLeaderProjectMembers[projectId];
          
          const allowedSet =
            membership?.memberIdsSet ||
            new Set(
              Array.isArray(membership?.memberIds)
                ? membership.memberIds.map((id) => Number(id))
                : []
            );

          const projectData = {};

          // Se l'utente è team leader su un progetto, mostra SOLO i dati dei membri del team
          // Il ruolo del progetto ha priorità sul ruolo globale
          if (membership && allowedSet.size > 0) {
            let url = `${API_BASE_URL}/monthly_worklog_summary_user_project?project_id=${encodeURIComponent(
              project.id
            )}&year_month=${yearMonth}`;
            if (activityParam) {
              url += activityParam;
            }

            const response = await fetch(url);
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`API Error: ${response.status} - ${errorText}`);
            }
            const data = await response.json();
            const entries = Array.isArray(data.data) ? data.data : [];

            entries.forEach((item) => {
              const userIdValue = Number(item.user_id);
              if (!allowedSet.has(userIdValue)) {
                return;
              }
              const day = new Date(item.work_date).getDate();
              if (!projectData[day]) {
                projectData[day] = {
                  total_effort: 0,
                  project_id: project.id,
                  project_name: project.name,
                  work_date: item.work_date,
                };
              }
              projectData[day].total_effort += Number(item.total_effort) || 0;
            });
          }

          byProject[project.name] = projectData;
        }
        */
        
        // TEMPORARY: TL usa la stessa logica dei manager (TUTTI gli inserimenti)
        if (teamLeaderProjects.length > 0) {
          // Usa la stessa API aggregata dei manager
          const teamLeaderProjectIds = teamLeaderProjects.map((p) => p.id);
          const requestBody = {
            year_month: yearMonth,
            project_ids: teamLeaderProjectIds,
          };

          if (filterActivityIds && filterActivityIds.length > 0) {
            requestBody.activity_ids = filterActivityIds;
          }
        
          const response = await fetch(`${API_BASE_URL}/monthly_project_effort_summary`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
        
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} - ${errorText}`);
          }
        
          const data = await response.json();
          if (Array.isArray(data.data)) {
            data.data.forEach((item) => {
              const projectName = item.project_name || (item.project && item.project.name) || 'Unknown Project';
              if (!byProject[projectName]) byProject[projectName] = {};
              const day = new Date(item.work_date).getDate();
              byProject[projectName][day] = item;
            });
          }
        }

        // Assicura che tutti i progetti selezionati siano presenti (anche se vuoti)
        selectedProjects.forEach((project) => {
          if (!byProject[project.name]) {
            byProject[project.name] = {};
          }
        });

        setPivotData(byProject);
        isFetchingPivotData.current = false;
      } catch (err) {
        console.error('Error in fetchPivotData:', err);
        setPivotError(`Error loading data: ${err.message}`);
        isFetchingPivotData.current = false;
      } finally {
        setPivotLoading(false);
      }
    };
    
    fetchPivotDataByProjectRole();
  }, [
    projectsInitialized,
    filterMonth,
    filterYear,
    projects,
    filterProjects,
    isAdmin,
    isDeliveryManager,
    isDeliveryManagerDirect,
    isServiceManager,
    isTeamLeader,
    isJRServiceManager,
    filterActivityIds,
    projectsLoaded,
    teamLeaderMembersLoaded,
    teamLeaderProjectMembers,
  ]);

  // Tabella dettagliata: fetch pivot per progetto selezionato
  useEffect(() => {
    if (!selectedProjectForDetails) {
      setTeamDetailsLoading(false);
      setProjectTeams([]); // Svuota i team quando non c'è progetto selezionato
      return;
    }
    
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const projectId = projects.find(p => p.name === selectedProjectForDetails)?.id || selectedProjectForDetails;
    const activityIdsKey = (filterActivityIds && filterActivityIds.length > 0) ? filterActivityIds.sort().join(',') : 'all';
    const fetchKey = `${projectId}-${yearMonth}-${activityIdsKey}`;

    // Evita chiamate duplicate per la stessa combinazione di parametri
    if (isFetchingProjectDetails.current) {
      return;
    }

    // Se la chiave è la stessa, evita la chiamata (dati già caricati)
    if (lastProjectDetailsKey.current === fetchKey) {
      return;
    }

    // Reset del flag se la chiave è diversa (nuovi parametri)
    if (lastProjectDetailsKey.current && lastProjectDetailsKey.current !== fetchKey) {
      isFetchingProjectDetails.current = false;
    }

    isFetchingProjectDetails.current = true;
    lastProjectDetailsKey.current = fetchKey;
    
    // Svuota i team IMMEDIATAMENTE quando cambia il progetto selezionato per evitare di mostrare team non autorizzati
    setProjectTeams([]);
    setTeamMembershipByUsername({});
    
    // Imposta loading state
    setTeamDetailsLoading(true);
    
    // Determina il ruolo dell'utente per questo progetto specifico
    const currentProject = projects.find(p => p.id === projectId);
    const isTeamLeaderForThisProject = currentProject ? isTeamLeaderForProject(currentProject) : false;
    
    // ============================================================================
    // [TL_FILTER] PROJECT DETAILS FILTERING - COMMENTED OUT
    // Questa logica filtra i dettagli del progetto basandosi sui membri del team.
    // Ora il TL vede tutti gli utenti come un Service Manager.
    // ============================================================================
    /*
    const teamLeaderMembership = isTeamLeaderForThisProject ? teamLeaderProjectMembers[projectId] : null;
    const allowedTeamLeaderSet =
      teamLeaderMembership?.memberIdsSet ||
      (teamLeaderMembership?.memberIds
        ? new Set(teamLeaderMembership.memberIds.map((id) => Number(id)))
        : new Set());

    if (isTeamLeaderForThisProject && (!teamLeaderMembership || allowedTeamLeaderSet.size === 0)) {
      setDetailsPivotData({});
      setProjectUsers([]);
      setProjectTeams([]);
      setTeamMembershipByUsername({});
      setTeamPivotData({});
      setTeamDetailsLoading(false);
      isFetchingProjectDetails.current = false;
      return;
    }
    */
    
    // Fetch worklog data (passa activity_ids se selezionate)
    const activityIdsParam = (filterActivityIds && filterActivityIds.length > 0)
      ? `&activity_ids=${encodeURIComponent(filterActivityIds.join(','))}`
      : '';
    fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?project_id=${encodeURIComponent(projectId)}&year_month=${yearMonth}${activityIdsParam}`)
        .then(res => res.json())
        .then(data => {
          // data: [{ username, work_date, total_effort, ... }]
          const byUser = {};
          if (Array.isArray(data.data)) {
            data.data.forEach(item => {
              // ============================================================================
              // [TL_FILTER] FILTERING IN PROJECT DETAILS - COMMENTED OUT
              // ============================================================================
              /*
              if (isTeamLeaderForThisProject) {
                const userIdValue = Number(item.user_id);
                if (!allowedTeamLeaderSet.has(userIdValue)) {
                  return;
                }
              }
              */
              // TEMPORARY: Mostra TUTTI gli utenti per TL
              if (!byUser[item.username]) byUser[item.username] = {};
              const day = new Date(item.work_date).getDate();
              byUser[item.username][day] = item;
            });
          }
        setDetailsPivotData(byUser);
      })
      .catch(err => {
        console.error('Error loading details:', err);
        isFetchingProjectDetails.current = false;
      });

    // Fetch project users with full details (ottimizzato - una sola chiamata)
    fetch(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${encodeURIComponent(projectId)}&include_full_details=true`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data.project_users)) {
            // Filter users with valid validity_end_date in the selected month
            const monthStart = new Date(filterYear, filterMonth - 1, 1);
            
            // I dati già includono tutti i dettagli utente, quindi possiamo filtrare direttamente
            const validUsers = data.project_users.filter(item => {
              // La struttura cambia se include_full_details=true
              const validityEndDate = item.validity_end_date;
              
              if (!validityEndDate) return true; // No end date = always valid
              const endDate = new Date(validityEndDate);
              return endDate >= monthStart; // User is valid if end date is >= month start
            });
            
            // Converti nella struttura attesa dal frontend
            const formattedUsers = validUsers
              .map(item => {
              // Se include_full_details=true, la struttura è { user, people_manager, role, project_role, ... }
              if (item.user) {
                return item.user; // Usa direttamente l'oggetto user
              }
              // Fallback per struttura legacy
              return item;
              })
              // ============================================================================
              // [TL_FILTER] USER FILTERING - COMMENTED OUT
              // ============================================================================
              /*
              .filter(user => {
                if (!isTeamLeaderForThisProject) return true;
                const userIdValue = Number(user?.id);
                return allowedTeamLeaderSet.has(userIdValue);
            })
              */
              // TEMPORARY: TL vede TUTTI gli utenti, nessun filtraggio
              ;
            
            setProjectUsers(formattedUsers);
          }
        })
        .catch(err => {
          console.error('Error loading project users:', err);
          isFetchingProjectDetails.current = false;
        })

    // Fetch project teams with members included (ottimizzato - una sola chiamata)
    const buildTeamMembership = async () => {
      try {
        // ============================================================================
        // [TL_FILTER] USER_ID PARAMETER REMOVED - Team Leader ora vede TUTTI i team
        // Prima: passava user_id per Team Leader, filtrando solo i suoi team
        // Ora: non passa user_id, mostrando tutti i team del progetto
        // ============================================================================
        
        // I team sono già stati svuotati all'inizio del useEffect, quindi non serve svuotarli di nuovo qui
        
        // TEMPORARY: Non passare user_id, Team Leader vede TUTTI i team come Service Manager
        const teamsRes = await fetch(`${API_ENDPOINTS.TEAMS}?project_id=${encodeURIComponent(projectId)}&include_members=true`);
        if (!teamsRes.ok) {
          setProjectTeams([]);
          setTeamMembershipByUsername({});
          setTeamDetailsLoading(false);
          isFetchingProjectDetails.current = false;
          return;
        }
        const teamsData = await teamsRes.json();
        // Il filtro è ora gestito completamente dal backend, quindi usiamo direttamente i team restituiti
        const teams = teamsData.teams || [];
        setProjectTeams(teams);

        // I membri sono già inclusi nella risposta e filtrati dal backend, costruisci direttamente la mappatura
        const membership = {};
        teams.forEach(team => {
          // Se include_members=true, i membri sono già in team.members e già filtrati dal backend
          if (team.members && Array.isArray(team.members)) {
            team.members.forEach(member => {
              const user = member.user || member;
              const username =
                user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
              if (!username) return;
              if (!membership[username]) {
                membership[username] = team.name || 'Team';
              }
            });
          }
        });
        setTeamMembershipByUsername(membership);
        isFetchingProjectDetails.current = false;
      } catch (e) {
        console.error('Error loading teams with members:', e);
        setProjectTeams([]);
        setTeamMembershipByUsername({});
        isFetchingProjectDetails.current = false;
      } finally {
        setTeamDetailsLoading(false);
      }
    };
    buildTeamMembership();
  }, [selectedProjectForDetails, filterMonth, filterYear, projects, filterActivityIds, teamLeaderProjectMembers, userId]);

  // Calcola la pivot per Team aggregando i valori per username nel rispettivo team
  useEffect(() => {
    if (!selectedProjectForDetails) return;
    if (!detailsPivotData || Object.keys(detailsPivotData).length === 0) {
      setTeamPivotData({});
      return;
    }
    const aggregated = {};
    Object.entries(detailsPivotData).forEach(([username, days]) => {
      const teamName = teamMembershipByUsername[username];
      if (!teamName) return; // considera solo utenti assegnati ad almeno un team
      if (!aggregated[teamName]) aggregated[teamName] = {};
      Object.entries(days).forEach(([day, obj]) => {
        const prev = aggregated[teamName][day]?.total_effort || 0;
        const add = Number(obj?.total_effort) || 0;
        aggregated[teamName][day] = { ...aggregated[teamName][day], total_effort: prev + add };
      });
    });
    setTeamPivotData(aggregated);
  }, [detailsPivotData, teamMembershipByUsername, selectedProjectForDetails]);

  // Funzione per renderizzare la tabella pivot dettagliata per risorsa

  // Helper: verifica che i team corrispondano al progetto selezionato
  const getValidTeamsForProject = (teams, projectId) => {
    if (!projectId) return teams; // Fallback se project_id non disponibile
    return teams.filter(team => team.project_id === projectId);
  };

  // Componente di loading riutilizzabile
  const TeamDetailsLoading = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      py: 4,
      bgcolor: 'white',
      borderRadius: 2,
      border: '1px solid #f3f3f3'
    }}>
      <CircularProgress size={24} sx={{ mr: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Loading team details...
      </Typography>
    </Box>
  );

  // Tabella pivot per Team (aggregata per team, come per utenti)
  function renderTeamPivotTable({ teamPivotData, filterYear, filterMonth, selectedProjectForDetails, projectTeams }) {
    const currentProjectId = projects.find(p => p.name === selectedProjectForDetails)?.id;
    const validTeams = getValidTeamsForProject(projectTeams || [], currentProjectId);
    
    // Verifica che i dati corrispondano al progetto selezionato
    // Se ci sono team ma nessuno corrisponde al progetto, i dati sono del progetto precedente
    const hasValidTeams = validTeams.length > 0 || (projectTeams || []).length === 0;
    
    // Non uscire subito: anche senza team e senza dati aggregati, possiamo mostrare "No team"
    const teamsWithData = teamPivotData ? Object.keys(teamPivotData) : [];
    
    // Controlla se ci sono utenti non assegnati a team (solo se i dati corrispondono al progetto)
    const usersWithoutTeam = hasValidTeams ? Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]) : [];
    const projectUsersWithoutTeam = hasValidTeams ? (projectUsers || [])
      .map(u => u.username || u.name || (u.first_name && u.last_name ? u.first_name + ' ' + u.last_name : u.email))
      .filter(u => !teamMembershipByUsername[u]) : [];
    const hasUsersWithoutTeam = hasValidTeams && (usersWithoutTeam.length > 0 || projectUsersWithoutTeam.length > 0);
    
    const allTeams = [...new Set([
      ...teamsWithData,
      ...(validTeams.map(t => t.name).filter(Boolean)),
      ...(hasUsersWithoutTeam ? ['No team'] : []) // Aggiungi "No team" solo se ci sono utenti non assegnati
    ])].sort();
    if (allTeams.length === 0) return null;

    const daysInMonth = new Date(filterYear, filterMonth, 0).getDate();
    const weekDays = Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
    const weekendIndexes = weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay();
      return (day === 0 || day === 6);
    });

    const teamTotals = allTeams.map(t => {
      if (t === 'No team') {
        // Calcola totali per utenti non assegnati a team
        const usersWithoutTeam = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
        return Math.round((usersWithoutTeam.reduce((sum, u) => {
          const userDays = detailsPivotData[u] || {};
          return sum + Object.values(userDays).reduce((daySum, obj) => daySum + (Number(obj?.total_effort) || 0), 0);
        }, 0) / 60) * 100) / 100;
      } else if (teamPivotData && teamPivotData[t]) {
        return Math.round((Object.values(teamPivotData[t]).reduce((a, b) => a + (Number(b?.total_effort) || 0), 0) / 60) * 100) / 100;
      }
      return 0;
    });
    
    const dayTotals = Array.from({ length: daysInMonth }, (_, i) => {
      let total = 0;
      allTeams.forEach(t => {
        if (t === 'No team') {
          // Calcola totali giornalieri per utenti non assegnati a team
          const usersWithoutTeam = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
          total += usersWithoutTeam.reduce((sum, u) => {
            const dayData = detailsPivotData[u]?.[i + 1];
            return sum + (Number(dayData?.total_effort) || 0);
          }, 0);
        } else {
          total += Number(teamPivotData?.[t]?.[i + 1]?.total_effort) || 0;
        }
      });
      return Math.round((total / 60) * 100) / 100;
    });
    const grandTotal = Math.round(teamTotals.reduce((a, b) => a + b, 0) * 100) / 100;

    // Assenze a livello team (aggregato): pastello tenue, distinto dalle celle risorsa
    const teamAbsFullBg = '#fce4ec';
    const teamAbsFullHover = '#f8d7e4';
    const teamAbsPartialBg = '#fff8e7';
    const teamAbsPartialHover = '#fff0d9';

    const getUsersInTeam = (teamLabel) => {
      if (teamLabel === 'No team') {
        const usersWithWorklog = Object.keys(detailsPivotData || {}).filter((u) => !teamMembershipByUsername[u]);
        const allProjectUsers = (projectUsers || [])
          .map((u) => u.username || u.name || (u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email))
          .filter((u) => !teamMembershipByUsername[u]);
        return Array.from(new Set([...usersWithWorklog, ...allProjectUsers]));
      }
      return Array.from(
        new Set([
          ...Object.keys(detailsPivotData || {}).filter((u) => teamMembershipByUsername[u] === teamLabel),
          ...(projectUsers || [])
            .map((u) => u.username || u.name || (u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email))
            .filter((u) => teamMembershipByUsername[u] === teamLabel),
        ])
      );
    };

    const getTeamAbsenceForDay = (teamLabel, dayNum) => {
      const users = getUsersInTeam(teamLabel);
      let hasFull = false;
      let hasPartial = false;
      const detailParts = [];
      users.forEach((u) => {
        const e = isUserAbsent(u, dayNum);
        if (!e) return;
        if (e.full_day) {
          hasFull = true;
          detailParts.push(`${formatUserName(u)}: full day`);
        } else {
          hasPartial = true;
          detailParts.push(`${formatUserName(u)}: partial`);
        }
      });
      if (!hasFull && !hasPartial) return null;
      return {
        full_day: hasFull,
        tooltip: detailParts.join('; '),
      };
    };

    return (
      <TableContainer component={Paper} sx={{ 
        borderRadius: 2, 
        bgcolor: 'white', 
        p: 0, 
        maxWidth: '100vw', 
        overflowX: 'auto', 
        boxShadow: 'none', 
        border: '1px solid #f3f3f3', 
        mt: 4
      }}>
        <Table size="small" sx={{ 
          minWidth: tableDimensions.minWidth, 
          tableLayout: 'fixed', 
          fontSize: '0.85rem', 
          width: '100%', 
          bgcolor: 'white'
        }}>
          <colgroup>
            <col style={{ width: tableDimensions.firstColWidth }} />
            {Array.from({ length: daysInMonth }).map((_, i) => (
              <col key={i} style={{ width: tableDimensions.dayColWidth }} />
            ))}
            <col style={{ width: tableDimensions.totalColWidth }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{ 
                bgcolor: '#5ab031', 
                color: 'white', 
                fontWeight: 'normal', 
                fontSize: '0.85rem', 
                border: 'none', 
                borderRight: '0px solid #e0e0e0', 
                textAlign: 'center', 
                minWidth: 120, 
                maxWidth: 160, 
                width: 140,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>
                Team
              </TableCell>
              {weekDays.map((wd, i) => (
                <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
              ))}
              <TableCell rowSpan={2} sx={{ 
                bgcolor: '#5ab031', 
                color: 'white', 
                fontWeight: 'normal', 
                fontSize: '0.85rem', 
                p: 0.5, 
                border: '0px solid #f3f3f3', 
                position: 'sticky', 
                right: 0, 
                zIndex: 3, 
                textAlign: 'center',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
            </TableRow>
            <TableRow>
              {Array.from({ length: daysInMonth }, (_, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allTeams.map((t, idx) => (
              <>
              <TableRow
                key={t}
                hover
                sx={{ '&:hover': { bgcolor: '#e6f4ea' }, bgcolor: selectedTeamsForDetails.includes(t) ? '#eef7e9' : undefined }}
                onClick={() => {
                  setSelectedTeamsForDetails(prev => 
                    prev.includes(t) 
                      ? prev.filter(team => team !== t)
                      : [...prev, t]
                  );
                }}
              >
                <MuiTooltip title={selectedTeamsForDetails.includes(t) ? 'Click to collapse' : 'Click to expand'} arrow>
                  <TableCell 
                    className="pivot-user-col"
                    sx={{ 
                      position: 'sticky', 
                      left: 0, 
                      zIndex: 2, 
                      minWidth: 120, 
                      maxWidth: 160, 
                      width: 140, 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderRight: '2px solid #5ab031', 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      textAlign: 'right',
                      bgcolor: 'white',
                      cursor: 'pointer',
                      userSelect: 'none',
                      textDecoration: 'underline',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {selectedTeamsForDetails.includes(t) ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      <span style={{ fontStyle: t === 'No team' ? 'italic' : 'normal' }}>{t}</span>
                    </Box>
                  </TableCell>
                </MuiTooltip>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  let value;
                  if (t === 'No team') {
                    // Calcola valore giornaliero per utenti non assegnati a team
                    const usersWithoutTeam = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
                    const dayTotal = usersWithoutTeam.reduce((sum, u) => {
                      const dayData = detailsPivotData[u]?.[i + 1];
                      return sum + (Number(dayData?.total_effort) || 0);
                    }, 0);
                    value = dayTotal;
                  } else {
                    value = teamPivotData?.[t]?.[i + 1]?.total_effort;
                  }
                  const displayValue = value ? Math.round((Number(value) / 60) * 100) / 100 : '';
                  const dayNum = i + 1;
                  const teamAbsEntry = getTeamAbsenceForDay(t, dayNum);
                  const isWeekend = weekendIndexes[i];
                  const cellBg = teamAbsEntry
                    ? (teamAbsEntry.full_day ? `${teamAbsFullBg} !important` : `${teamAbsPartialBg} !important`)
                    : (isWeekend ? 'rgba(55,55,55,0.08)' : 'white');
                  const tooltipTitle = teamAbsEntry
                    ? `Absence — ${teamAbsEntry.tooltip}${displayValue ? ` · ${displayValue}h` : ''}`
                    : '';
                  const dayHoverBg = teamAbsEntry
                    ? (teamAbsEntry.full_day ? `${teamAbsFullHover} !important` : `${teamAbsPartialHover} !important`)
                    : '#aace7c78';
                  return (
                    <MuiTooltip key={i + 1} title={tooltipTitle} arrow>
                    <TableCell sx={{ 
                      textAlign: 'center', 
                      bgcolor: cellBg, 
                      fontSize: '0.7rem', 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontVariantNumeric: 'tabular-nums',
                      '&:hover': { bgcolor: dayHoverBg } 
                    }}>{teamAbsEntry && !displayValue ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: teamAbsEntry.full_day ? '#a87d8f' : '#b89a6e', verticalAlign: 'middle', opacity: 0.9 }} /> : displayValue}</TableCell>
                    </MuiTooltip>
                  );
                })}
                <TableCell sx={{ 
                  textAlign: 'center', 
                  fontSize: '0.7rem', 
                  p: 0.5, 
                  border: '1px solid #f3f3f3', 
                  position: 'sticky', 
                  right: 0, 
                  zIndex: 2, 
                  borderLeft: '2px solid #5ab031',
                  minWidth: 60,
                  maxWidth: 80,
                  width: 60,
                  bgcolor: 'white !important',
                  color: 'black !important',
                  fontWeight: 'bold',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>{teamTotals[idx]}</TableCell>
              </TableRow>
              {selectedTeamsForDetails.includes(t) && (
                <>
                  {/* Righe degli utenti */}
                  {(() => {
                    let usersInTeam;
                    if (t === 'No team') {
                      // Utenti non assegnati a nessun team (inclusi quelli senza worklog)
                      const usersWithWorklog = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
                      const allProjectUsers = (projectUsers || [])
                        .map(u => u.username || u.name || (u.first_name && u.last_name ? u.first_name + ' ' + u.last_name : u.email))
                        .filter(u => !teamMembershipByUsername[u]);
                      
                      usersInTeam = Array.from(new Set([
                        ...usersWithWorklog,
                        ...allProjectUsers
                      ])).sort();
                    } else {
                      // Utenti del team (presenti nei dati dettagliati o, se mancanti, dai projectUsers)
                      usersInTeam = Array.from(new Set([
                        ...Object.keys(detailsPivotData || {}).filter(u => teamMembershipByUsername[u] === t),
                        ...((projectUsers || [])
                          .map(u => u.username || u.name || (u.first_name && u.last_name ? u.first_name + ' ' + u.last_name : u.email))
                          .filter(u => teamMembershipByUsername[u] === t))
                      ])).sort();
                    }

                    const userRowTotal = (u) => {
                      const days = detailsPivotData?.[u] || {};
                      return Math.round((Object.values(days).reduce((sum, obj) => sum + (Number(obj?.total_effort) || 0), 0) / 60) * 100) / 100;
                    };

                    return usersInTeam.map(u => (
                      <TableRow key={u} sx={{ bgcolor: '#f9fbf8' }}>
                        <MuiTooltip title={u} arrow>
                        <TableCell sx={{ 
                            fontSize: '0.8rem', 
                            borderRight: '2px solid #5ab031',
                            borderTop: '1px solid #f3f3f3',
                            borderBottom: '1px solid #f3f3f3',
                            borderLeft: '1px solid #f3f3f3',
                            minWidth: 120,
                            maxWidth: 160,
                            width: 140,
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                          bgcolor: 'white'
                          }}>{formatUserName(u)}</TableCell>
                        </MuiTooltip>
                        {Array.from({ length: daysInMonth }, (_, i) => {
                          const val = detailsPivotData?.[u]?.[i + 1]?.total_effort;
                          const display = val ? Math.round((Number(val) / 60) * 100) / 100 : '';
                          const isWeekend = weekendIndexes[i];
                          const dayNum = i + 1;
                          const absEntry = isUserAbsent(u, dayNum);
                          return (
                            <MuiTooltip key={i+1} title={absEntry ? `Absence (${absEntry.full_day ? 'full day' : 'partial'})${display ? ` · ${display}h` : ''}` : ''} arrow>
                            <TableCell sx={{ 
                              textAlign: 'center', 
                              fontSize: '0.7rem', 
                              p: 0.5, 
                              border: '1px solid #f3f3f3',
                              bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : 'white',
                              fontWeight: 'normal',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontVariantNumeric: 'tabular-nums',
                              '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                            }}>
                              {absEntry && !display ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: absEntry.full_day ? '#c62828' : '#f57f17', verticalAlign: 'middle' }} /> : display}
                            </TableCell>
                            </MuiTooltip>
                          );
                        })}
                        <TableCell sx={{ 
                          textAlign: 'center', 
                          fontSize: '0.7rem', 
                          p: 0.5, 
                          borderLeft: '2px solid #5ab031',
                          borderTop: '1px solid #f3f3f3',
                          borderBottom: '1px solid #f3f3f3',
                          borderRight: '1px solid #f3f3f3',
                          minWidth: 60,
                          maxWidth: 80,
                          width: 60,
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          bgcolor: 'white !important',
                          color: 'black !important',
                          fontWeight: 'bold'
                        }}>{userRowTotal(u)}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </>
              )}
              </>
            ))}
            {/* Totali per giorno */}
            <TableRow sx={{ bgcolor: '#248b7e' }}>
              <TableCell 
                className="pivot-user-col"
                sx={{ 
                  bgcolor: 'white', 
                  position: 'sticky', 
                  left: 0, 
                  zIndex: 2, 
                  minWidth: 120, 
                  maxWidth: 160, 
                  width: 140, 
                  p: 0.5, 
                  border: '1px solid #f3f3f3', 
                  borderRight: '2px solid #5ab031', 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  textAlign: 'right', 
                  borderTop: '2px solid #5ab031',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>Tot</TableCell>
              {dayTotals.map((tot, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{tot}</TableCell>
              ))}
              <TableCell sx={{ 
                bgcolor: 'white !important', 
                color: 'black !important', 
                textAlign: 'center', 
                fontSize: '0.7rem', 
                p: 0.5, 
                border: '1px solid #f3f3f3', 
                position: 'sticky', 
                right: 0, 
                zIndex: 2, 
                borderLeft: '2px solid #5ab031', 
                borderTop: '2px solid #5ab031',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                fontWeight: 'bold',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // L'API ora filtra i progetti lato server, quindi usiamo direttamente i dati ricevuti
  // Ordina i progetti alfabeticamente per la visualizzazione
  const filteredPivotData = useMemo(() => {
    console.log('Using pivot data from API:', Object.keys(pivotData));
    // Ordina i progetti alfabeticamente
    const sortedEntries = Object.entries(pivotData).sort(([a], [b]) => 
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    return Object.fromEntries(sortedEntries);
  }, [pivotData]);

  // Calcola totali per riga e per giorno (basati sui dati filtrati, convertiti da minuti a ore)
  const rowTotals = useMemo(() => {
    const totals = {};
    Object.entries(filteredPivotData).forEach(([rowKey, days]) => {
      totals[rowKey] = Math.round((Object.values(days).reduce((sum, obj) => sum + (Number(obj?.total_effort) || 0), 0) / 60) * 100) / 100;
    });
    return totals;
  }, [filteredPivotData]);
  const dayTotals = useMemo(() => {
    const totals = {};
    for (let d = 1; d <= daysInMonth; d++) {
      totals[d] = Math.round((Object.values(filteredPivotData).reduce((sum, days) => sum + (Number(days[d]?.total_effort) || 0), 0) / 60) * 100) / 100;
    }
    return totals;
  }, [filteredPivotData, daysInMonth]);
  const grandTotal = useMemo(() => Math.round(Object.values(rowTotals).reduce((sum, v) => sum + v, 0) * 100) / 100, [rowTotals]);

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>Monthly Tracking View</Typography>
        </Box>
        {/* Filtri progetto e intervallo date */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Autocomplete
              multiple
            disabled={!projectsLoaded && (isServiceManager || isTeamLeader || isJRServiceManager || isAdmin)}
            disableCloseOnSelect={true}
            options={[
              { id: 'all', name: 'All', isSpecial: true },
              ...projects
            ]}
            getOptionLabel={(option) => option.name}
            value={projects.filter(p => filterProjects.includes(p.name))}
            onChange={(event, newValue) => {
              // Handle special options
              const hasAll = newValue.some(v => v.id === 'all');
              
              if (hasAll) {
                setFilterProjects([]);
              } else {
                setFilterProjects(newValue.map(p => p.name));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
              label="Projects"
                placeholder="Search projects..."
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={option.id === 'all' ? filterProjects.length === 0 : false}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }
              
              return (
                <Box component="li" {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {option.name}
                    </Typography>
                    {option.customer?.name && (
                      <Typography variant="caption" color="textSecondary">
                        {option.customer.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  All projects
                </Typography>
              ) : value.length === 1 && value[0].id === 'all' ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'black' }}>
                  All
                </Typography>
              ) : (
                <Typography variant="body2">
                  {value.length} selected
                </Typography>
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No projects found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              // Always show special options at the top
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              
              // Filter regular options based on input
              const filteredRegular = regularOptions.filter(option =>
                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.customer?.name && option.customer.name.toLowerCase().includes(inputValue.toLowerCase()))
              );
              
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="filter-month-label">Month</InputLabel>
            <Select
              labelId="filter-month-label"
              value={filterMonth}
              label="Month"
              onChange={e => setFilterMonth(Number(e.target.value))}
            >
              {monthNames.map((name, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Filtro Activity */}
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            options={[
              { id: 'all', name: 'All', isSpecial: true },
              ...activities.map(ac => ({ id: ac.id, name: ac.name }))
            ]}
            getOptionLabel={(option) => option.name}
            value={activities
              .filter(ac => filterActivityIds.includes(ac.id))
              .map(ac => ({ id: ac.id, name: ac.name }))}
            onChange={(event, newValue) => {
              const hasAll = newValue.some(v => v.id === 'all');
              if (hasAll) {
                setFilterActivityIds([]);
              } else {
                setFilterActivityIds(newValue.map(v => v.id));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Activities"
                placeholder="Search activities..."
                size="small"
                sx={{ minWidth: 260 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={option.id === 'all' ? filterActivityIds.length === 0 : false}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box component="li" {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              );
            }}
            renderTags={(value) =>
              value.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  All activities
                </Typography>
              ) : value.length === 1 && value[0].id === 'all' ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'black' }}>
                  All
                </Typography>
              ) : (
                <Typography variant="body2">{value.length} selected</Typography>
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No activities found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              const filteredRegular = regularOptions.filter(option =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              );
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel id="filter-year-label">Year</InputLabel>
            <Select
              labelId="filter-year-label"
              value={filterYear}
              label="Year"
              onChange={e => setFilterYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={() => { setFilterProjects([]); setFilterMonth(now.getMonth() + 1); setFilterYear(now.getFullYear()); setSelectedProjectForDetails(null); }}>Reset</Button>
        </Box>
        {canExportAllProjectsAbsences && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={exportAllAbsencesLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={exportAllAbsencesLoading || exportAbsencesLoading || pivotLoading}
              onClick={handleExportAllProjectsAbsences}
            >
              {exportAllAbsencesLoading ? 'Exporting...' : 'Export absences (all projects)'}
            </Button>
          </Box>
        )}
        {(!projectsLoaded && (isServiceManager || isTeamLeader || isJRServiceManager)) ? (
          <CircularProgress />
        ) : pivotLoading ? (
          <CircularProgress />
        ) : pivotError ? (
          <Alert severity="error">{pivotError}</Alert>
        ) : (Object.keys(filteredPivotData).length === 0) ? (
          <Alert severity="info">{filterProjects.length > 0 ? `No worklogs found for the selected project(s) in this period.` : `For this date, no worklogs are present.`}</Alert>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ 
              borderRadius: 2, 
              bgcolor: 'white', 
              p: 0, 
              maxWidth: '100vw', 
              overflowX: 'auto', 
              boxShadow: 'none', 
              border: '1px solid #f3f3f3'
            }}>
              <Table size="small" sx={{ 
                minWidth: tableDimensions.minWidth, 
                tableLayout: 'fixed', 
                fontSize: '0.85rem', 
                width: '100%', 
                bgcolor: 'white'
              }}>
              <colgroup>
                <col style={{ width: tableDimensions.firstColWidth }} />
                {Array.from({ length: daysInMonth }).map((_, i) => (
                  <col key={i} style={{ width: tableDimensions.dayColWidth }} />
                ))}
                <col style={{ width: tableDimensions.totalColWidth }} />
              </colgroup>
              <TableHead>
                <TableRow>
                    <TableCell
                      className="pivot-user-col"
                      rowSpan={2}
                      sx={{
                        bgcolor: '#5ab031',
                        border: 'none',
                        p: 0,
                        minWidth: 120,
                        maxWidth: 160,
                        width: 140,
                        color: 'white',
                        fontWeight: 'normal',
                        borderRight: '0px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      Project
                  </TableCell>
                  {weekDays.map((wd, i) => {
                    const isWeekend = weekendIndexes[i];
                    return (
                      <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
                    );
                  })}
                    <TableCell sx={{ 
                      bgcolor: '#5ab031', 
                      color: 'white', 
                      fontWeight: 'normal', 
                      fontSize: '0.85rem', 
                      p: 0.5, 
                      border: '0px solid #f3f3f3', 
                      position: 'sticky', 
                      right: 0, 
                      zIndex: 3, 
                      verticalAlign: 'middle', 
                      textAlign: 'center',
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 3,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }} rowSpan={2}>Tot</TableCell>
                </TableRow>
                <TableRow>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const isWeekend = weekendIndexes[i];
                    return (
                        <TableCell key={i+1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i+1}</TableCell>
                    );
                  })}
                  {/* Nessuna TableCell qui per Tot, perché la cella sopra ha rowSpan=2 */}
                </TableRow>
              </TableHead>
              <TableBody>
                  {Object.entries(filteredPivotData).map(([rowKey, days]) => {
                    // Per DELIVERY_MANAGER non c'è più espansione inline, quindi isExpanded è sempre false
                    const isExpanded = (isDeliveryManager || isDeliveryManagerDirect) 
                      ? false
                      : rowKey === selectedProjectForDetails;
                    const isDeliveryManagerRole = isDeliveryManager || isDeliveryManagerDirect;
                    
                    return (
                      <React.Fragment key={rowKey}>
                    <TableRow
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#e6f4ea' },
                            bgcolor: isExpanded ? '#dff3e1' : undefined,
                            borderLeft: isExpanded ? '3px solid #5ab031' : '3px solid transparent'
                      }}
                    >
                          <MuiTooltip title={isExpanded ? 'Click to collapse' : 'Click to expand'} arrow>
                        <TableCell 
                          className="pivot-user-col"
                          sx={{ 
                            bgcolor: 'white', 
                                color: isExpanded ? '#2e7d32' : '#5ab031',
                            position: 'sticky', 
                            left: 0, 
                            zIndex: 2,
                            minWidth: 120, 
                            maxWidth: 160, 
                            width: 140, 
                            p: 0.5, 
                            border: '1px solid #f3f3f3', 
                            borderRight: '2px solid #5ab031', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                            cursor: 'pointer',
                                fontWeight: isExpanded ? 'bold' : 'normal',
                            textDecoration: 'underline',
                            '&:hover': {
                              bgcolor: '#e6f4ea',
                              color: '#4a9b2a',
                              fontWeight: 'bold'
                            },
                            '@media (max-width: 900px)': {
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            }
                          }}
                              onClick={() => {
                                if (isDeliveryManagerRole) {
                                  // Per DELIVERY_MANAGER: naviga a una nuova pagina con i dettagli
                                  const project = projects.find(p => p.name === rowKey);
                                  if (project) {
                                    navigate(`/monthly-mng-view/project/${project.id}`, {
                                      state: {
                                        filterYear,
                                        filterMonth,
                                        filterActivityIds
                                      }
                                    });
                                  }
                                } else {
                                  // Per altri ruoli: toggle (apri/chiudi)
                                  setSelectedProjectForDetails(prev => 
                                    prev === rowKey ? null : rowKey
                                  );
                                }
                              }}
                            >
                              <span>{rowKey}</span>
                        </TableCell>
                      </MuiTooltip>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const obj = days[i + 1];
                        const value = obj?.total_effort ? Math.round((Number(obj.total_effort) / 60) * 100) / 100 : '';
                        const isWeekend = weekendIndexes[i];
                        return (
                          <TableCell key={i + 1} sx={{ textAlign: 'center', bgcolor: isWeekend ? 'rgba(55,55,55,0.08)' : 'white', color: '#374151', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', fontWeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums', '&:hover': { bgcolor: '#aace7c78' } }}>
                            {obj ? (
                              <MuiTooltip
                                title={
                                  <span>
                                    {obj.ticket_id && <div><b>Ticket:</b> {obj.ticket_id}</div>}
                                    {obj.description && <div><b>Desc:</b> {obj.description}</div>}
                                    {obj.activity_name && <div><b>Activity:</b> {obj.activity_name}</div>}
                                    {obj.status_name && <div><b>Status:</b> {obj.status_name}</div>}
                                    {obj.effort_type_name && <div><b>Effort Type:</b> {obj.effort_type_name}</div>}
                                    {obj.total_effort && <div><b>Effort:</b> {Math.round((Number(obj.total_effort) / 60) * 100) / 100} h</div>}
                                  </span>
                                }
                                arrow
                              >
                                <span>{value}</span>
                              </MuiTooltip>
                            ) : value}
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ 
                        bgcolor: 'white !important', 
                        color: 'black !important', 
                        textAlign: 'center', 
                        fontSize: '0.7rem', 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderLeft: '2px solid #5ab031', 
                        position: 'sticky', 
                        right: 0, 
                        zIndex: 2,
                        minWidth: 60,
                        maxWidth: 80,
                        width: 60,
                        fontWeight: 'bold',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>{rowTotals[rowKey]}</TableCell>
                    </TableRow>
                    {/* Dettagli espansi inline per DELIVERY_MANAGER */}
                    {isDeliveryManagerRole && isExpanded && (
                      <TableRow>
                        <TableCell 
                          colSpan={daysInMonth + 2}
                          sx={{ 
                            p: 0, 
                            border: 'none',
                            bgcolor: '#f9fbf8'
                          }}
                        >
                          {(() => {
                            // Verifica che i dati corrispondano al progetto corrente
                            const currentProjectId = projects.find(p => p.name === rowKey)?.id;
                            const isCurrentProjectSelected = selectedProjectForDetails === rowKey;
                            
                            // Se i dati non corrispondono al progetto corrente, mostra loading
                            if (!isCurrentProjectSelected || teamDetailsLoading) {
                              return <TeamDetailsLoading />;
                            }
                            
                            // Verifica che i team corrispondano al progetto selezionato prima di renderizzare
                            const validTeams = getValidTeamsForProject(projectTeams || [], currentProjectId);
                            const teamsMatchProject = !currentProjectId || validTeams.length === (projectTeams || []).length;
                            
                            // Se i team non corrispondono al progetto, mostra loading per evitare di mostrare dati del progetto precedente
                            if (!teamsMatchProject && projectTeams && projectTeams.length > 0) {
                              return <TeamDetailsLoading />;
                            }
                            
                            return renderTeamPivotTable({ teamPivotData, filterYear, filterMonth, selectedProjectForDetails: rowKey, projectTeams });
                          })()}
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                  })}
                  {/* Riga totale per giorno (ripristinata) */}
                <TableRow sx={{ bgcolor: '#248b7e' }}>
                  <TableCell 
                      sx={{  
                        bgcolor: 'white', 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 2, 
                        minWidth: 120, 
                        maxWidth: 160, 
                        width: 140, 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderRight: '2px solid #5ab031', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        textAlign: 'right', 
                        borderTop: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }} 
                    className="pivot-user-col"
                  >
                    Tot
                  </TableCell>
                  {Array.from({ length: daysInMonth }, (_, i) => (
                      <TableCell key={i+1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{dayTotals[i+1]}</TableCell>
                    ))}
                    <TableCell sx={{ 
                      bgcolor: 'white !important', 
                      color: 'black !important', 
                      textAlign: 'center', 
                      fontSize: '0.7rem', 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderLeft: '2px solid #5ab031', 
                      borderTop: '2px solid #5ab031', 
                      position: 'sticky', 
                      right: 0, 
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}>{grandTotal}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
            {/* Tabella dettagliata per progetto selezionato (solo per ruoli diversi da DELIVERY_MANAGER) */}
            {selectedProjectForDetails && !(isDeliveryManager || isDeliveryManagerDirect) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={exportAbsencesLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    disabled={
                      exportAbsencesLoading ||
                      teamDetailsLoading ||
                      !projectUsers?.length
                    }
                    onClick={handleExportProjectAbsences}
                  >
                    {exportAbsencesLoading ? 'Exporting...' : 'Export absences'}
                  </Button>
                </Box>
                {teamDetailsLoading ? (
                  <TeamDetailsLoading />
                ) : (() => {
                  // Verifica che i team corrispondano al progetto selezionato prima di renderizzare
                  const currentProjectId = projects.find(p => p.name === selectedProjectForDetails)?.id;
                  const validTeams = getValidTeamsForProject(projectTeams || [], currentProjectId);
                  const teamsMatchProject = !currentProjectId || validTeams.length === (projectTeams || []).length;
                  
                  // Se i team non corrispondono al progetto, mostra loading per evitare di mostrare dati del progetto precedente
                  if (!teamsMatchProject && projectTeams && projectTeams.length > 0) {
                    return <TeamDetailsLoading />;
                  }
                  
                  return renderTeamPivotTable({ teamPivotData, filterYear, filterMonth, selectedProjectForDetails, projectTeams });
                })()}
              </Box>
            )}
          </>
        )}
        {/* Note about effort in hours */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mt: 1, 
          pr: 1 
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#666', 
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}
          >
            * effort in hours
          </Typography>
        </Box>
      </Box>
      
      {/* Informational note */}
      <Box sx={{ 
        mt: 3, 
        p: 2, 
        bgcolor: 'grey.50', 
        borderRadius: 1, 
        border: '1px solid', 
        borderColor: 'grey.200' 
      }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontStyle: 'italic',
            textAlign: 'center'
          }}
        >
          💡 <strong>Tip:</strong> Click on a project name to view detailed hours breakdown for team members associated with that project for the selected month.
        </Typography>
      </Box>
      </Box>
  );
} 